import { useCallback, useEffect, useRef, useState } from 'react';
import { ProjectFile } from '../types';
import {
  buildVerifierRunPayload,
  formatVerifierErrorForLog,
  generatePromptTexOutput,
  getVerifierPayloadSizeBytes,
  getVerifierStatusForError,
  getVerifierStatusFromExit,
  getPromptTexFile,
  normalizeVerifierPayloadError,
  normalizeVerifierPromptError,
  resolveVerifierFileContents,
  selectVerifierFiles,
  streamVerifierRun,
} from '../services/verifier';
import { formatBytes } from '../services/projects';
import {
  VerifierRunPhase,
  VerifierRunStatus,
  VerifierStreamEvent,
  VerifierUserFacingError,
} from '../types/verifier';

interface UseVerifierRunnerOptions {
  projectId?: string;
  files: ProjectFile[];
}

interface UseVerifierRunnerReturn {
  clear: () => void;
  entrypoint: string | null;
  error: string | null;
  errorDetails?: VerifierUserFacingError | null;
  events: VerifierStreamEvent[];
  phase?: VerifierRunPhase;
  run: (activeFile: ProjectFile | null, canRun: boolean, model?: string) => Promise<void>;
  running: boolean;
  snapshotCapturedAt: Date | null;
  status: VerifierRunStatus;
  stop: () => void;
}

export function useVerifierRunner({
  projectId,
  files,
}: UseVerifierRunnerOptions): UseVerifierRunnerReturn {
  const [entrypoint, setEntrypoint] = useState<string | null>(null);
  const [events, setEvents] = useState<VerifierStreamEvent[]>([]);
  const [status, setStatus] = useState<VerifierRunStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState<VerifierUserFacingError | null>(null);
  const [phase, setPhase] = useState<VerifierRunPhase>('idle');
  const [snapshotCapturedAt, setSnapshotCapturedAt] = useState<Date | null>(null);
  const activeRunRef = useRef<{ id: number; controller: AbortController } | null>(null);
  const nextRunIdRef = useRef(0);

  const appendAbortEvent = useCallback(() => {
    setEvents((current) => {
      const lastEvent = current[current.length - 1];
      if (lastEvent?.type === 'system' && lastEvent.text === 'Run aborted by user.\n') {
        return current;
      }

      return [
        ...current,
        { type: 'system', text: 'Run aborted by user.\n' },
      ];
    });
  }, []);

  const clear = useCallback(() => {
    setEntrypoint(null);
    setEvents([]);
    setError(null);
    setErrorDetails(null);
    setPhase('idle');
    setStatus('idle');
    setSnapshotCapturedAt(null);
  }, []);

  const stop = useCallback(() => {
    const activeRun = activeRunRef.current;
    if (!activeRun) {
      return;
    }

    activeRun.controller.abort();
    activeRunRef.current = null;
    setError(null);
    setErrorDetails(null);
    setStatus('aborted');
    appendAbortEvent();
  }, [appendAbortEvent]);

  const createAbortError = useCallback(() => {
    const abortError = new Error('Run aborted by user.');
    abortError.name = 'AbortError';
    return abortError;
  }, []);

  const appendTrailingNewline = useCallback((text: string) => (
    text.endsWith('\n') ? text : `${text}\n`
  ), []);

  const run = useCallback(async (activeFile: ProjectFile | null, canRun: boolean, model?: string) => {
    if (!projectId) {
      setError('Project ID is missing.');
      setErrorDetails(null);
      setPhase('idle');
      setStatus('failed');
      return;
    }

    const ext = activeFile?.name.toLowerCase() ?? '';
    if (!activeFile || (!ext.endsWith('.py') && !ext.endsWith('.lean'))) {
      setError('Select a Python (.py) or Lean (.lean) file to run the verifier.');
      setErrorDetails(null);
      setPhase('idle');
      setStatus('failed');
      return;
    }

    if (!canRun) {
      setError('You need access to this project or reviewer, admin, or owner privileges to run this verifier.');
      setErrorDetails(null);
      setPhase('idle');
      setStatus('failed');
      return;
    }

    activeRunRef.current?.controller.abort();

    const controller = new AbortController();
    const runId = nextRunIdRef.current + 1;
    nextRunIdRef.current = runId;
    activeRunRef.current = { id: runId, controller };
    setEntrypoint(activeFile.name);
    setEvents([
      { type: 'system', text: 'Running prompt.tex through the model...\n' },
    ]);
    setError(null);
    setErrorDetails(null);
    setPhase('prompt');
    setStatus('starting');
    setSnapshotCapturedAt(new Date());

    let currentPhase: VerifierRunPhase = 'prompt';
    const isActiveRun = () => activeRunRef.current?.id === runId;
    try {
      const promptFile = getPromptTexFile(files);
      const promptResult = await generatePromptTexOutput(projectId, promptFile.content, model);

      if (controller.signal.aborted || !isActiveRun()) {
        throw createAbortError();
      }

      setEvents((current) => [
        ...current,
        {
          type: 'system',
          text: `prompt.tex output${promptResult.model ? ` (${promptResult.model})` : ''}:\n`,
        },
        {
          type: 'stdout',
          text: appendTrailingNewline(promptResult.output),
        },
      ]);

      currentPhase = 'verifier';
      setPhase('verifier');

      // Select and resolve files for the verifier
      const { selected, needsStorageFetch } = selectVerifierFiles(activeFile, files);

      let resolvedFiles = selected;
      if (needsStorageFetch.length > 0) {
        setEvents((current) => [
          ...current,
          { type: 'system', text: `Downloading ${needsStorageFetch.length} data file(s) from storage...\n` },
        ]);
        resolvedFiles = await resolveVerifierFileContents(selected, files, (fileName, sizeBytes) => {
          setEvents((current) => [
            ...current,
            { type: 'system', text: `  ${fileName} (${formatBytes(sizeBytes)})...\n` },
          ]);
        });
      }

      if (controller.signal.aborted || !isActiveRun()) {
        throw createAbortError();
      }

      const payload = buildVerifierRunPayload(projectId, activeFile.name, resolvedFiles, {
        stdinText: promptResult.output,
        allFiles: files,
      });

      const payloadSize = getVerifierPayloadSizeBytes(payload);
      setEvents((current) => [
        ...current,
        {
          type: 'system',
          text: `Uploading ${formatBytes(payloadSize)} (${payload.files.length} file(s)) to verifier...\n`,
        },
      ]);

      await streamVerifierRun(payload, {
        signal: controller.signal,
        onEvent: (event) => {
          if (!isActiveRun()) {
            return;
          }

          setEvents((current) => [...current, event]);
          if (event.type === 'started') {
            setPhase('verifier');
            setStatus('running');
            return;
          }

          if (event.type === 'exit') {
            setStatus(getVerifierStatusFromExit(event));
          }
        },
      });

      if (!isActiveRun()) {
        return;
      }

      setStatus((current) => (
        current === 'starting' || current === 'running'
          ? 'failed'
          : current
      ));
    } catch (runError) {
      if (controller.signal.aborted || (runError as Error).name === 'AbortError') {
        if (isActiveRun()) {
          setStatus('aborted');
          appendAbortEvent();
        }
        return;
      }

      if (!isActiveRun()) {
        return;
      }

      const structuredError = currentPhase === 'prompt'
        ? normalizeVerifierPromptError(runError)
        : normalizeVerifierPayloadError(runError);
      const message = structuredError
        ? formatVerifierErrorForLog(structuredError)
        : runError instanceof Error
          ? runError.message
          : 'Failed to run the verifier.';

      setEvents((current) => [
        ...current,
        { type: 'system', text: `${message}\n` },
      ]);
      setError(structuredError ? structuredError.message : message);
      setErrorDetails(structuredError);
      setStatus(structuredError ? getVerifierStatusForError(structuredError) : 'failed');
    } finally {
      if (activeRunRef.current?.id === runId) {
        activeRunRef.current = null;
      }
    }
  }, [appendAbortEvent, appendTrailingNewline, createAbortError, files, projectId]);

  useEffect(() => {
    return () => {
      activeRunRef.current?.controller.abort();
      activeRunRef.current = null;
    };
  }, []);

  return {
    clear,
    entrypoint,
    error,
    errorDetails,
    events,
    phase,
    run,
    running: status === 'starting' || status === 'running',
    snapshotCapturedAt,
    status,
    stop,
  };
}
